import { Link, useNavigate } from 'react-router-dom';

import { useColors, useResponsive } from '@inbody/core';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Breadcrumbs, Typography } from '@mui/material';

import type { BreadcrumbCrumb } from './MainLayout';

import { Urls } from '@/shared/lib/react-router/Urls';

interface HeaderBreadcrumbProps {
  crumbs: BreadcrumbCrumb[];
}

/**
 * 헤더 브레드크럼 — 눌러서 이동할 수 있다.
 *
 * 같은 헤더를 쓰는 사내 앱끼리 브레드크럼만 달라 보이면 다른 시스템처럼 읽힌다.
 * MUI `Breadcrumbs` 에 `NavigateNextIcon` 구분자, 12px `caption` 글자, 마지막 조각만 진하게(gray[700] · 500) 구성
 *
 * 다른 점은 **이동이 붙는다**는 것뿐이다. 공용 `LayoutHeader` 의
 * `breadcrumbText` 는 문자열을 그대로 그려 클릭이 되지 않고, 그 컴포넌트는 다른
 * 앱도 함께 쓰므로 손대지 않고 `leftActions` 로 넣는다.
 *
 * 마지막 조각(현재 화면)과 그룹 이름은 링크로 만들지 않는다. 지금 있는 곳을
 * 다시 누르게 하거나, 갈 화면이 없는 그룹을 누를 수 있게 하면 눌러도 아무 일이
 * 없어 링크가 고장 난 것처럼 보이기 때문이다.
 */
export const HeaderBreadcrumb = ({ crumbs }: HeaderBreadcrumbProps) => {
  const { gray, brand } = useColors();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();

  if (crumbs.length === 0) return null;

  /** 조각 공통 글자 — insa-portal 의 브레드크럼 Typography 와 같은 값. */
  const crumbSx = {
    fontSize: 12,
    lineHeight: 1,
  } as const;

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon sx={{ fontSize: 14, color: gray[400] }} />}
      aria-label="breadcrumb"
      sx={{
        minWidth: 0,
        '& .MuiBreadcrumbs-ol': { alignItems: 'center', flexWrap: 'nowrap' },
        '& .MuiBreadcrumbs-li': {
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
        },
        '& .MuiBreadcrumbs-separator': {
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      {/* 홈 — insa-portal 은 아이콘만 두지만, 여기서는 눌러서 이동한다. */}
      <Box
        component="button"
        type="button"
        aria-label="홈으로 이동"
        onClick={() => navigate(Urls.main.path)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          border: 'none',
          bgcolor: 'transparent',
          p: 0,
          cursor: 'pointer',
          color: gray[400],
          '&:hover': { color: brand[600] },
        }}
      >
        <HomeIcon sx={{ fontSize: 14, display: 'block' }} />
      </Box>

      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;

        // 현재 화면과 그룹은 링크가 아니다 — 위 주석 참고.
        if (isLast || !crumb.url) {
          return (
            <Typography
              key={`${crumb.label}-${index}`}
              variant="caption"
              sx={{
                ...crumbSx,
                color: isLast ? gray[700] : gray[500],
                fontWeight: isLast ? 500 : 400,
                ...(isMobile && {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 120,
                }),
              }}
            >
              {crumb.label}
            </Typography>
          );
        }

        return (
          <Typography
            key={`${crumb.label}-${index}`}
            component={Link}
            to={crumb.url}
            variant="caption"
            sx={{
              ...crumbSx,
              color: gray[500],
              fontWeight: 400,
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': { color: brand[600], textDecoration: 'underline' },
            }}
          >
            {crumb.label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
};
